/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  ACTION_BUSINESS_EXPERIENCE_SEARCH_RESULT_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import {
  ACTION_SELECT_BOOKING_TIME,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { CurrencyContext, LocaleContext, consumer, core } from '@tock/coretypes';
import { DateTimeFormatter, LocalDateTime } from 'js-joda';
import { Dialog, DialogContent, DialogHeading } from 'omakase';
import { Head } from 'head/Head';
import { IntlProvider } from 'react-intl';
import { LocationDescriptor } from 'history';
import { LockOptions } from 'types';
import { OfferingDetails } from 'components/business/offering/OfferingDetails';
import { Route, StaticContext, useLocation, useRouteMatch } from 'react-router';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { businessToData, experienceToData } from 'utils/analytics/amplitude';
import {
  convertTicketTypeToParams,
  isSupportedApplePayEnvironment,
} from 'hooks/apple-pay/useIsSupportedApplePayEnvironment';
import { find, includes, isEmpty, isEqual, toInteger, toNumber, toString } from 'es-toolkit/compat';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getFirstExperienceImage } from 'head/experienceImages';
import { getLockedDateTimeFor, getLockedSeatingAreaFor } from 'utils/lock';
import { getTitleSuffix } from 'utils/seo';
import {
  isGAEventType,
  isOfferedOnOneDateAndAtOneTime,
  isOfferingEventType,
} from 'utils/offerings';
import { isQuickCheckoutEligibleExperience } from 'utils/quickCheckoutUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useBusinessCalendar } from 'hooks/calendar/useBusinessCalendar';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useFeatureFlags } from '@tock/shared/utils/featureFlagContext';
import EventPreCheckoutDetails from 'components/business/search/EventPreCheckoutDetails';
import ExperienceNotFound from 'components/business/search/ExperienceNotFound';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import OfferingsStatusMessage from 'components/business/offering/OfferingsStatusMessage';
import ReservationPreCheckoutDetails from 'components/business/search/ReservationPreCheckoutDetails';
import SearchFooter from 'components/business/search/SearchFooter';
import SearchLoading from 'components/business/search/SearchLoading';
import clsx from 'clsx';
import queryString from '@tock/query-string';

interface Props {
  business?: consumer.IBusinessConfig;
  calendar: StoreType['calendar'];
  offerings?: consumer.ConsumerOfferings;
  experienceId?: number;
  experienceInfo?: core.ITicketType;
  getUrlPostfix(experience: core.ITicketType, type: 'EXPERIENCE' | 'MENU'): string;
  isAuthenticated: boolean;
  isOpen: boolean;
  isLoading?: boolean;
  isPrivateOffering?: boolean;
  isSearching: boolean;
  isRoute: boolean;
  isWidget: boolean;
  lock: StoreType['lock'];
  nextRelease?: core.IBookingRelease;
  onClose(): void;
  onAfterClose?(): void;
  onSearch(location: LocationDescriptor): void;
  onSelectTime(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.IBusinessConfig
  ): Promise<void>;
  onShowError(error: string): void;
  query?: Dictionary<string>;
  staticContext?: StaticContext;
}

interface MatchParams {
  businessDomain: string;
  widget?: string;
}

const ExperienceModal: React.FunctionComponent<Props> = ({
  business,
  offerings,
  experienceId,
  getUrlPostfix,
  isAuthenticated,
  isOpen,
  isLoading,
  isPrivateOffering = false,
  isSearching,
  isRoute,
  isWidget,
  lock,
  nextRelease,
  onClose,
  onSearch,
  onSelectTime,
  onShowError,
  onAfterClose,
  query,
  staticContext,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const {
    actions: { calendar },
  } = useActions();
  const isFeatureFlagEnabled = useFeatureFlags();
  const isEventLikeExperiencesEnabled = useFeatureFlagForConsumer('EVENT_LIKE_EXPERIENCES');
  const fetchCalendarDates = useFeatureFlagForConsumer('FETCH_CALENDAR_DATES');

  const {
    selectedTicketType,
    search: {
      requestInProgress: isCalendarLoading,
      calendarPresent,
      isValidQuery: validQuery,
      params: { partySize: selectedPartySize },
    },
    getRemainingTicketsForEvent,
  } = useBusinessCalendar({ ticketTypeId: experienceId });

  React.useEffect(() => {
    if (fetchCalendarDates) {
      // This component is always mounting on SearchResultsPage > SearchExperienceModal so we need to gate it to only fetch when we have a business in context
      business &&
        calendar.fetchDates({
          ticketTypeId: selectedTicketType?.id || undefined,
          businessId: business.id || undefined,
        });
    }
  }, [business, fetchCalendarDates, calendar, selectedTicketType?.id]);

  const { params } = useRouteMatch<MatchParams>();
  const location = useLocation();

  const experience = selectedTicketType;

  const widgetPathMod = () => {
    return isWidget ? '/widget' : '';
  };

  const rootRef = React.useRef<HTMLDivElement>(null);

  const handleSearch = (size?: number, date?: string, time?: string, seatingAreaId?: number) => {
    const query = readQuery();
    const timeToQuery = time || query.time;

    if (!experience) {
      return;
    }

    const queryParams = {
      date,
      size: toString(size),
      time: timeToQuery,
      cameFrom: query.cameFrom,
      showExclusives: query.showExclusives ? 'true' : undefined,
      seatingAreaId: seatingAreaId,
    };
    const postfix = getUrlPostfix(experience, 'EXPERIENCE');
    if (!isEqual(query, queryParams)) {
      if (fetchCalendarDates) {
        calendar.fetchDates({
          partySize: size,
          ticketTypeId: experience?.id || undefined,
          businessId: business?.id || undefined,
        });
      }
      onSearch({
        pathname: `/${params.businessDomain}${widgetPathMod()}${postfix}`,
        search: queryString.stringify(queryParams),
      });
    }
  };

  const getWaitlistUrl = () => {
    const query = readQuery();
    const { date, size } = query;

    const queryParams: Dictionary<string> = {
      experienceId: toString(experienceId),
      size: toString(size),
      date,
      fromPage: 'experience',
    };

    if (seatingAreaId) {
      queryParams.seatingAreaId = toString(seatingAreaId);
    }

    return `/${params.businessDomain}${widgetPathMod()}/waitlist?${queryString.stringify(
      queryParams
    )}`;
  };

  const handleNavigateToNextDate = (nextDate: string, experience: core.ITicketType) => {
    const query = readQuery();
    const date = nextDate ? nextDate : query.date;

    return handleSearch(query.size, date, query.time, query.seatingAreaId);
  };

  const handleSelectTime = (dateTime: LocalDateTime, priceCents: number, options: LockOptions) => {
    const query = readQuery();
    const { size: partySize } = query;

    const ticketTypeId = toInteger(experienceId);

    if (!partySize) {
      onShowError('Select the number of guests to continue');
      return Promise.reject();
    }

    if (!business) {
      return Promise.resolve();
    }

    const selectedSize = toInteger(partySize);

    window.analytics.sendEvent(
      CATEGORY_BUSINESS_PAGE,
      ACTION_BUSINESS_EXPERIENCE_SEARCH_RESULT_CLICKED
    );

    const isLoginRequired = experience
      ? !isQuickCheckoutEligibleExperience(experience, priceCents, isFeatureFlagEnabled, partySize)
      : undefined;

    const seatingAreaId = options.seatingAreaId;
    const selectedSeatingArea = offering?.seatingArea.find(
      (seatingArea) => seatingArea.id === seatingAreaId
    )?.name;

    const supportedApplePayEnvironment = isSupportedApplePayEnvironment(
      convertTicketTypeToParams(experience)
    );

    const analyticsData: EventCustomAttributes = {
      ...businessToData(business),
      ...experienceToData(experience),
      reservationDateTime: dateTime.format(DateTimeFormatter.ofPattern('yyyy-MM-dd hh:mm:ss')),
      partySize: partySize,
      isLoginRequired,
      hasAddOns: !isEmpty(experience?.optionGroup || []),
      seatingAreaId,
      seatingArea: selectedSeatingArea || undefined,
      isSupportedApplePayEnvironment: supportedApplePayEnvironment,
    };

    amplitudeSendEvent(ACTION_SELECT_BOOKING_TIME, analyticsData);
    ga4SendEvent(ACTION_SELECT_BOOKING_TIME, analyticsData);

    return onSelectTime(ticketTypeId, selectedSize, dateTime, priceCents, options, business);
  };

  const readQuery = () => {
    const queryParams = query ? query : queryString.parse(location.search);
    return {
      date: queryParams.date,
      time: queryParams.time,
      size: queryParams.size ? Number(queryParams.size) : undefined,
      cameFrom: queryParams.cameFrom,
      showExclusives: queryParams.showExclusives === 'true',
      seatingAreaId: queryParams.seatingAreaId ? Number(queryParams.seatingAreaId) : undefined,
    };
  };

  const handleBack = () => {
    window.history.back();
  };

  let { date, cameFrom } = readQuery();
  const { seatingAreaId } = readQuery();
  // For server side rendering, default to today. This allows us to deep link experience page
  // to crawlers,
  // On the client side the lack of defined params cause a html5 router redirect to the
  // appropriate date.
  const defaultDateTime = LocalDateTime.now().plusHours(1);

  const offering = find(
    offerings?.experience as consumer.Offering[],
    (experience) => experience.id === toNumber(experienceId)
  );

  if (!BROWSER) {
    date = date || defaultDateTime.toLocalDate().toString();
    cameFrom = cameFrom || '';
  }

  const isSearchLoading = isCalendarLoading && !calendarPresent;

  /**
   * A private offering can lack an experience.  If we have not found the experience info
   * once the calendar has loaded it means that the offering is unavailable.
   * In that case we want to display the ExperienceNotFound messaging vs the search error.
   */
  const hasSearchError = (!isPrivateOffering && !experienceId) || !offerings;
  const showLoadingOrErrorState = isSearchLoading || hasSearchError;

  const showNoBookingsState = (!calendarPresent || !offerings) && !isCalendarLoading;

  const description = offering?.description ?? '';

  const heading = business?.name ?? '';

  const showBack = cameFrom === 'search_modal' && !isWidget;

  const sharedProps = {
    heading,
    isOpen,
    isRoute,
    isWidget,
    onBack: handleBack,
    onClose,
    onAfterClose,
    showBack,
  };
  const isAtOneDateTime = isOfferedOnOneDateAndAtOneTime(offering);
  const isOfferingEvent = isOfferingEventType(offering);
  const useEventCard = isAtOneDateTime && (isOfferingEvent || isEventLikeExperiencesEnabled);
  const remainingEventTickets =
    useEventCard && calendarPresent && offering && offering.id && isGAEventType(offering.type)
      ? getRemainingTicketsForEvent(offering.id)
      : undefined;

  const resultComponent = (
    <>
      {business && offering && (
        <div
          className={clsx({
            'Consumer-resultsDescriptionAndDetails': isOfferingEvent,
          })}
        >
          <OfferingDetails
            business={business}
            offering={offering}
            remainingTickets={remainingEventTickets}
            includeAccessKey={false}
          />
        </div>
      )}
    </>
  );

  // todo pull this logic out into a function that returns early instead of variable reassignment hell
  let dialogContent;

  if (showLoadingOrErrorState && isOpen) {
    const loadingDialogContent = (
      <>
        {resultComponent}
        <SearchLoading
          isAuthenticated={isAuthenticated}
          isError={!experienceId}
          isLoading={isCalendarLoading}
          location={location}
        />
      </>
    );

    if (!offering && staticContext) {
      (staticContext as any).status = 404;
      (staticContext as any).soft = true;
    }
    dialogContent = loadingDialogContent;
  }

  if (!showLoadingOrErrorState && showNoBookingsState) {
    dialogContent = (
      <>
        {resultComponent}
        <div className="SearchModal-body">
          <OfferingsStatusMessage businessName={business?.name ?? ''} nextRelease={nextRelease} />
        </div>
      </>
    );
  }

  if (!showLoadingOrErrorState && (!validQuery || !calendarPresent)) {
    dialogContent = (
      <>
        {resultComponent}
        <SearchLoading
          isAuthenticated={isAuthenticated}
          isError={!validQuery}
          isLoading={isCalendarLoading}
          location={location}
        />
      </>
    );
  }

  const isPrivate = includes(offerings?.privateExperienceId, toNumber(experienceId));

  if (!showLoadingOrErrorState && !experience) {
    sharedProps.heading =
      !isLoading && experience && offerings
        ? isPrivate
          ? isOfferingEvent
            ? 'Private event'
            : 'Private experience'
          : 'Experience not found'
        : sharedProps.heading;
    dialogContent = (
      <>
        <ExperienceNotFound
          businessEmail={business?.email ?? ''}
          businessName={business?.name ?? ''}
          isPrivateExperience={isPrivate}
          isWidget={isWidget}
          location={location}
          isAuthenticated={isAuthenticated}
          isOfferingEvent={isOfferingEvent}
          onButtonClick={onClose}
        />
      </>
    );
  }

  if (!showLoadingOrErrorState && experience && business) {
    const ticketTypeId = toNumber(experienceId);

    const selectedDate = date;
    const acceptsWalkins = business?.acceptsWalkins ?? false;

    const className = clsx('SearchModalExperience', {
      'is-animating': !isSearching,
    });

    let lockedDateTime;
    let lockedSeatingArea;

    if (lock.currentLock) {
      lockedDateTime = getLockedDateTimeFor(
        lock.currentLock,
        experience.id as number,
        selectedPartySize
      );
      lockedSeatingArea = getLockedSeatingAreaFor(
        lock.currentLock,
        experience.id as number,
        selectedPartySize
      );
    }

    dialogContent = (
      <IntlProvider locale={(business as consumer.BusinessConfig).locale}>
        <LocaleContext.Provider value={(business as consumer.BusinessConfig).locale}>
          <CurrencyContext.Provider value={(business as consumer.BusinessConfig).currencyCode}>
            {resultComponent}
            {offering && useEventCard && (
              <EventPreCheckoutDetails
                business={business}
                event={offering}
                currentLock={lock.currentLock}
                onAttemptLock={handleSelectTime}
                onSearch={handleSearch}
                waitlistUrl={getWaitlistUrl()}
                selectedSize={selectedPartySize}
                ticketTypeId={ticketTypeId}
              />
            )}
            {offering && !useEventCard && (
              <ReservationPreCheckoutDetails
                business={business}
                className={className}
                experienceInfo={experience}
                hasInitialFocus
                isSearching={isSearching}
                lockedDateTime={lockedDateTime ?? undefined}
                lockedSeatingArea={lockedSeatingArea ?? undefined}
                offerings={offerings ?? undefined}
                onSearch={handleSearch}
                onSelectTime={handleSelectTime}
                waitlistUrl={getWaitlistUrl()}
                selectedDate={selectedDate}
                selectedPurchaseSize={selectedPartySize}
                selectedSeatingArea={seatingAreaId}
                onNavigateToNextDate={handleNavigateToNextDate}
                ticketTypeIdOverride={experienceId}
              />
            )}
            <SearchFooter
              acceptsWalkins={!!acceptsWalkins}
              businessName={business.name ?? ''}
              businessEmail={business.email ?? ''}
              businessPhone={business.customerFacingPhone ?? ''}
            />
          </CurrencyContext.Provider>
        </LocaleContext.Provider>
      </IntlProvider>
    );
  }

  const offeringName = offering?.name;
  const businessName = business?.name ?? undefined;
  const htmlDocTitle =
    businessName &&
    `${businessName}${offeringName ? ` - ${offeringName}` : ''}${getTitleSuffix(business)}`;

  const checkAvailabilityAriaLabel =
    'Search availability' +
    (offeringName ? ` for ${offeringName}` : '') +
    (businessName ? ` at ${businessName}` : '');

  const isDialogOpen = sharedProps.isOpen;

  return (
    <div ref={rootRef}>
      {isDialogOpen && (
        <Head
          title={htmlDocTitle}
          description={description}
          image={getFirstExperienceImage(offering)}
          site={business?.twitterHandle ?? ''}
          siteName={businessName ?? ''}
        />
      )}
      <Dialog
        fullWidth
        aria-label={checkAvailabilityAriaLabel}
        open={sharedProps.isOpen}
        onClose={sharedProps.onClose}
        data-testid="experience-dialog"
      >
        <DialogHeading
          id="experience-dialog-title"
          onClose={sharedProps.onClose}
          data-testid="experience-modal-title"
        >
          {sharedProps.heading}
        </DialogHeading>
        <DialogContent disablePadding id="experience-dialog-content">
          <LoadingComponent
            isLoading={isLoading || lock.requestInProgress}
            showOverlay={!isLoading}
            isBallPulse={true}
          >
            {dialogContent}
          </LoadingComponent>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ExperienceModalWrapper: React.FunctionComponent<Props> = (props) => {
  return (
    <Route
      render={({ staticContext }) => {
        return <ExperienceModal {...props} staticContext={staticContext} />;
      }}
    />
  );
};

export default ExperienceModalWrapper;
